import React from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@pericles/framework-frontend";

import { useAuth } from "../hooks/auth";

import { Input, PageShell } from "./ui";

const SESSION_CONFIRMATION_ATTEMPTS = 8;
const SESSION_CONFIRMATION_DELAY_MS = 250;

export function LoginScreen(): JSX.Element {
  const navigate = useNavigate();
  const { user, setUser, refresh } = useAuth();
  const [email, setEmail] = React.useState("demo@pericles.local");
  const [password, setPassword] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  React.useEffect(() => {
    if (user) {
      navigate("/", { replace: true });
    }
  }, [navigate, user]);

  const confirmSession = React.useCallback(async (): Promise<boolean> => {
    for (let attempt = 0; attempt < SESSION_CONFIRMATION_ATTEMPTS; attempt += 1) {
      const authenticated = await refresh();
      if (authenticated) {
        return true;
      }
      if (attempt < SESSION_CONFIRMATION_ATTEMPTS - 1) {
        await new Promise((resolve) => window.setTimeout(resolve, SESSION_CONFIRMATION_DELAY_MS));
      }
    }
    return false;
  }, [refresh]);

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setIsSubmitting(true);
    try {
      const response = await fetch("/auth/login", {
        method: "POST",
        credentials: "same-origin",
        headers: { "content-type": "application/json", accept: "application/json" },
        body: JSON.stringify({ email, password }),
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        setError(payload?.error ?? "Login failed");
        return;
      }

      const sessionConfirmed = await confirmSession();
      if (!sessionConfirmed) {
        setUser(null);
        setError("Session bootstrap failed. Please retry.");
        return;
      }
      navigate("/", { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Login failed");
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <PageShell className="min-h-screen items-center justify-center bg-slate-50">
      <Card className="w-full max-w-md">
        <div className="flex flex-col gap-4 p-6">
          <div className="space-y-1 text-center">
            <h1 className="text-2xl font-semibold text-slate-900">LongWalls by Pericles</h1>
            <p className="text-sm font-semibold text-slate-600">From Ambition to Outcome.</p>
          </div>
          <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
            <div className="flex flex-col gap-3">
              <label className="flex flex-col gap-1 text-sm">
                Email
                <Input
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  required
                />
              </label>
              <label className="flex flex-col gap-1 text-sm">
                Password
                <Input
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  required
                />
              </label>
            </div>
            {error ? <p className="text-sm text-red-600">{error}</p> : null}
            <button
              type="submit"
              className="w-full button button-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Signing in…" : "Sign in"}
            </button>
          </form>
        </div>
      </Card>
    </PageShell>
  );
}
